/**
 * Shared UI primitives.
 *
 * Two of these carry rules worth stating, because they are the ones the brief
 * is explicit about:
 *
 *   - **`NeedsAttentionList`** requires a destination on every row. A row
 *     without one will not render and logs a warning in development. The
 *     alternative — a count that looks clickable and is not — is exactly the
 *     decorative dashboard the brief rules out.
 *
 *   - **`EmptyState`** requires a message, and takes an action. "No
 *     assignments yet" plus "Create assignment" is the contract; a blank
 *     panel is not an acceptable state.
 */

import type { MouseEvent, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IconAlert, IconChevronRight } from './icons'
import './ui.css'

/* ── LinkedRow ─────────────────────────────────────────────────────────── */

/**
 * A table row that is clickable everywhere, not only on its name link.
 *
 * The whole row navigates to `to`; a click that lands on a real control
 * inside the row (the name link itself, an action button, an input) still
 * belongs to that control, and dragging out a text selection is not treated
 * as a click. The name link stays in the markup as the keyboard and
 * middle-click path, so nothing is lost for anyone who cannot or does not
 * use a pointer.
 */
export function LinkedRow({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  const navigate = useNavigate()

  function onClick(event: MouseEvent<HTMLTableRowElement>) {
    const target = event.target as HTMLElement
    if (target.closest('a, button, input, select, textarea, label, [role="button"]')) return
    if (window.getSelection()?.toString()) return
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      window.open(to, '_blank', 'noopener')
      return
    }
    navigate(to)
  }

  return (
    <tr className={className ? `${className} row-link` : 'row-link'} onClick={onClick}>
      {children}
    </tr>
  )
}

/* ── Card ─────────────────────────────────────────────────────────────────── */

export function Card({
  title,
  icon,
  action,
  children,
  className = '',
  padded = true,
}: {
  title?: ReactNode
  icon?: ReactNode
  action?: { label: string; to: string }
  children: ReactNode
  className?: string
  padded?: boolean
}) {
  return (
    <section className={`card ${className}`}>
      {title && (
        <header className="card__header">
          <h2 className="card__title">
            {icon && <span className="card__icon">{icon}</span>}
            {title}
          </h2>
          {action && (
            <Link className="card__action" to={action.to}>
              {action.label}
              <IconChevronRight />
            </Link>
          )}
        </header>
      )}
      <div className={padded ? 'card__body' : 'card__body card__body--flush'}>{children}</div>
    </section>
  )
}

/* ── Stat tile ────────────────────────────────────────────────────────────── */

export function StatTile({
  icon,
  value,
  label,
  trend,
  tone = 'brand',
  to,
}: {
  icon?: ReactNode
  value: ReactNode
  label: string
  trend?: { direction: 'up' | 'down' | 'flat'; text: string }
  tone?: 'brand' | 'success' | 'critical' | 'warning'
  to?: string
}) {
  const body = (
    <>
      {icon && <span className={`stat__icon stat__icon--${tone}`}>{icon}</span>}
      <span className="stat__content">
        <span className="stat__value numeral">{value}</span>
        <span className="stat__label">{label}</span>
        {trend && (
          <span className={`stat__trend stat__trend--${trend.direction}`}>
            <span aria-hidden="true">{trend.direction === 'up' ? '▲' : trend.direction === 'down' ? '▼' : '—'}</span>
            {trend.text}
          </span>
        )}
      </span>
    </>
  )

  return to ? (
    <Link className="stat stat--link" to={to}>
      {body}
    </Link>
  ) : (
    <div className="stat">{body}</div>
  )
}

/* ── Status pill ──────────────────────────────────────────────────────────── */

export type Tone = 'success' | 'warning' | 'critical' | 'info' | 'neutral'

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`pill pill--${tone}`}>{children}</span>
}

/* ── Needs attention ──────────────────────────────────────────────────────── */

export interface AttentionRow {
  id: string
  label: string
  count: number
  severity: 'critical' | 'warning' | 'info'
  /** Where this row goes. A row without one is not rendered — see above. */
  to: string
}

export function NeedsAttentionList({ rows }: { rows: AttentionRow[] }) {
  const navigable = rows.filter((row) => {
    if (row.to) return true
    if (import.meta.env.DEV) {
      console.warn(
        `NeedsAttention row "${row.label}" has no destination and was not rendered. ` +
          'Every row on this card must lead to the list it describes.',
      )
    }
    return false
  })

  if (navigable.length === 0) {
    return (
      <EmptyState
        icon={<IconAlert />}
        title="Nothing needs attention"
        message="Attendance is submitted, grades are posted and no enrollments are incomplete."
      />
    )
  }

  return (
    <ul className="attention">
      {navigable.map((row) => (
        <li key={row.id}>
          <Link to={row.to} className="attention__row">
            <span className={`attention__dot attention__dot--${row.severity}`} aria-hidden="true" />
            <span className="attention__label">{row.label}</span>
            <span className="attention__count numeral">{row.count}</span>
            <IconChevronRight className="attention__chevron" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

/* ── Progress bar ─────────────────────────────────────────────────────────── */

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <div className="progress">
      <div
        className="progress__track"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="progress__fill" style={{ width: `${clamped}%` }} />
      </div>
      {/* The number is the content; the bar is the garnish. */}
      <span className="progress__value numeral">{clamped.toFixed(0)}%</span>
    </div>
  )
}

/* ── States ───────────────────────────────────────────────────────────────── */

/**
 * The nothing-here state, with at most one way out of it.
 *
 * `action` takes either a destination or a handler. It used to take only a
 * destination, so a screen whose action was to open a dialog passed `to: '#'`
 * and rendered its real button underneath — two identical buttons, the first
 * of which went nowhere. A dead control beside a live one is worse than no
 * control, and the brief rules it out explicitly.
 */
export function EmptyState({
  icon,
  title,
  message,
  action,
}: {
  icon?: ReactNode
  title: string
  message: string
  action?: { label: string; to: string; onClick?: never } | { label: string; onClick: () => void; to?: never }
}) {
  return (
    <div className="state">
      {icon && <span className="state__icon">{icon}</span>}
      <p className="state__title">{title}</p>
      <p className="state__message">{message}</p>
      {action &&
        (action.to ? (
          <Link className="button button--primary" to={action.to}>
            {action.label}
          </Link>
        ) : (
          <button className="button button--primary" type="button" onClick={action.onClick}>
            {action.label}
          </button>
        ))}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state state--error" role="alert">
      <span className="state__icon state__icon--error">
        <IconAlert />
      </span>
      <p className="state__title">Something went wrong</p>
      <p className="state__message">{message}</p>
      {onRetry && (
        <button type="button" className="button button--secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}

/**
 * A loading placeholder shaped like the content it replaces.
 *
 * `aria-busy` with a polite live region rather than a spinner, so a screen
 * reader is told the region is loading once instead of announcing a spinner
 * that never resolves into anything.
 */
export function Skeleton({ rows = 3, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div className="skeleton" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}…</span>
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} className="skeleton__row" />
      ))}
    </div>
  )
}

/* ── Section heading ──────────────────────────────────────────────────────── */

export function PageHeader({
  title,
  subtitle,
  aside,
}: {
  title: ReactNode
  subtitle?: ReactNode
  aside?: ReactNode
}) {
  return (
    <header className="page-header">
      <div className="page-header__text">
        <h1 className="page-header__title">{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {aside && <div className="page-header__aside">{aside}</div>}
    </header>
  )
}
